/**
 * MetadataFooter — quiet trace block at the bottom of the rail: the audit
 * record id (click to copy), engine processing time, and the automated
 * decision timestamp. Data that reviewers rarely need but auditors always ask
 * for — present, but not competing with the work surface.
 */

import { useState } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

import type { FinalDecisionCompact } from "../../api/client";
import { formatTimestamp } from "./VerdictBanner";

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export function MetadataFooter({ automated }: { automated: FinalDecisionCompact }) {
  const [copied, setCopied] = useState(false);

  const copyAuditId = () => {
    if (!automated.audit_id) return;
    void navigator.clipboard.writeText(automated.audit_id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1.5, px: 0.5 }}>
      <Stack spacing={0.5}>
        {automated.audit_id && (
          <MetaRow label="Registro de auditoría">
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  textAlign: "right",
                }}
              >
                {automated.audit_id}
              </Typography>
              <Tooltip title={copied ? "Copiado" : "Copiar"}>
                <IconButton
                  size="small"
                  aria-label="Copiar id del registro de auditoría"
                  onClick={copyAuditId}
                  sx={{ p: 0.25 }}
                >
                  {copied ? (
                    <CheckIcon sx={{ fontSize: 14 }} color="success" />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          </MetaRow>
        )}

        {typeof automated.processing_time_seconds === "number" && (
          <MetaRow label="Tiempo de procesamiento">
            <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {automated.processing_time_seconds.toFixed(1)} s
            </Typography>
          </MetaRow>
        )}

        <MetaRow label="Decisión del motor">
          <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {formatTimestamp(automated.timestamp)}
          </Typography>
        </MetaRow>
      </Stack>
    </Box>
  );
}
